import { useAsyncFn } from '@clara/hooks'
import type { COUNTRY } from '@clara/locale'
import { http } from '@/lib'

export type BankInfoResponse = {
  // Common fields
  uuid: string
  accountNumber: string
  name: string

  // MX specific fields
  taxPayerId?: string
  countryCode?: string

  // BR specific fields
  accountType?: 'PIX' | 'TED'
  accountSubType?: PixAccountType | 'CHECKING' | 'SAVINGS'
  branchCode?: string
  cpf?: string
  bankKey?: string

  // CO specific fields
  identificationType?: 'PASSPORT'
  identificationNumber?: string
  validationStatus: 'INVALID' | 'ERROR_STP' | 'STP_ACCOUNT'
}

export type UserInfoResponse = {
  name: string
  fullName: string
}

async function getBankInfo() {
  return http
    .get<Array<BankInfoResponse>>('/user-bank')
    .then((res) => res.data[0])
}

export function useGetBankInfo() {
  return useAsyncFn(async () => {
    const res = await getBankInfo()
    return res
  }, [])
}

async function getUserInfo() {
  return http
    .get<UserInfoResponse>('/user-bank/userInfo')
    .then((res) => res.data)
}

export function useGetUserInfo() {
  return useAsyncFn(async () => {
    const res = await getUserInfo()
    return res
  }, [])
}

export type PixAccountType = 'CPF' | 'EMAIL' | 'PHONE' | 'RANDOM_KEY'

export type BankAccountType = 'CHECKING' | 'SAVINGS'

export type CoIdentificationType =
  | 'PASSPORT'
  | 'FOREIGNER_ID'
  | 'CITIZENSHIP_ID'

export type BankInfo = {
  accountNumber: string
  taxPayerId: string
  countryCode: COUNTRY
}

export type PixPayload = {
  accountType: 'PIX'
  accountNumber: string
  accountSubType: PixAccountType
}

export type TedPayload = {
  accountType: 'TED'
  accountSubType: BankAccountType
  accountNumber: string
  branchCode: string
  cpf: string
  bankKey: string
}

export type COBankInfoPayload = {
  accountNumber: string
  accountType: BankAccountType
  identificationType: CoIdentificationType
  identificationNumber: string
  bankKey: string
}

type PostBankInfoPayload =
  | BankInfo
  | PixPayload
  | TedPayload
  | COBankInfoPayload

export async function postBankInfo(
  payload: PostBankInfoPayload,
  uuid?: string,
) {
  // TODO: type response
  return !!uuid
    ? http.put<any>('/user-bank', { ...payload, uuid }).then((res) => res.data)
    : http.post<any>('/user-bank', payload).then((res) => res.data)
}
